import { getThemeHtmlAttrs, getThemeStyles, type ThemeColorModes } from '@atlaskit/tokens';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { DEFAULT_THEME } from '@atlassian/jira-common-constants/src/theme-preference.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
import { loadThemeTokens } from '../../../utils/load-theme-tokens.tsx';
import type { ThemePreferenceResource } from '../types.tsx';
import { themeColorModes } from '../../../common/constants.tsx';
import { getThemePreference } from '../../../services/shared-preferences.tsx';
import { getThemePreference as getThemePreferenceOld } from '../../../services/index.tsx';

// Theme override through query parameter:
// Use case: when Jira is embedded as an iframe inside another app that may have a different theme.
// So far this is used by Confluence, see confluence/platform/packages/linking-platform/link-create-jira/
// Ex. if Confluence is light and Jira dark, Jira embedded in Confluence should be light regardless of the jira preference
export const getThemeOverrideFromQueryParameters = ({ query }: RouterDataContext) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	if (query?.themeMode && themeColorModes.includes(query.themeMode as ThemeColorModes)) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return query.themeMode as ThemeColorModes;
	}
	return undefined;
};

const getResourceReturns = async (theme: ThemeColorModes): Promise<ThemePreferenceResource> => {
	let typographyTheme:
		| 'typography-adg3'
		| 'typography-modernized'
		| 'typography-refreshed'
		| undefined;

	if (isVisualRefreshEnabled()) {
		typographyTheme = 'typography-refreshed';
	}

	const attrs = getThemeHtmlAttrs({ colorMode: theme, typography: typographyTheme });
	const styles = await getThemeStyles({ colorMode: theme, typography: typographyTheme });

	loadThemeTokens(theme);

	return {
		theme,
		attrs,
		styles,
	};
};

// WARNING This function is flawed, see parent README.md
export default async function getData(
	context: RouterDataContext,
	resourceStoreContext?: ResourceStoreContext | undefined,
): Promise<ThemePreferenceResource> {
	try {
		if (fg('shared-user-preferences')) {
			const { cloudId, activationId, atlassianAccountId } =
				resourceStoreContext?.tenantContext ?? {};

			const colorMode =
				getThemeOverrideFromQueryParameters(context) ||
				(await getThemePreference(cloudId, activationId, atlassianAccountId));
			return await getResourceReturns(colorMode);
		}

		// TODO (DST): Should be deleted along with shared-user-preferences cleanup
		const theme = getThemeOverrideFromQueryParameters(context) || (await getThemePreferenceOld());
		return await getResourceReturns(theme || DEFAULT_THEME);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		log.safeErrorWithoutCustomerData(
			'spa.router.resources.navigation.themePreferenceResource',
			'themePreferenceResource',
			err,
		);
		return {};
	}
}
